import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { filterMovies, PAGE_SIZE } from "@/lib/catalog";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useReveal } from "@/hooks/useReveal";

export default function Category() {
  const [params, setParams] = useSearchParams();
  const [mobileFilter, setMobileFilter] = useState(false);

  const genre = params.get("genre") ?? undefined;
  const region = params.get("region") ?? undefined;
  const year = params.get("year") ?? undefined;
  const sort = (params.get("sort") ?? "latest") as "latest" | "popular";
  const page = Number(params.get("page") ?? "1") || 1;

  usePageSeo({
    title: "电影分类 - 蜜桃成熟时电影",
    description: "浏览和筛选多种类型电影，快速找到感兴趣的电影资源。",
    keywords: "电影分类,电影筛选,电影类型,电影地区,电影年份,蜜桃成熟时电影",
  });

  const result = useMemo(
    () => filterMovies({ genre, region, year, sort }),
    [genre, region, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = result.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  useReveal([genre, region, year, sort, current]);

  const updateParams = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v) next.set(k, v);
      else next.delete(k);
    });
    setParams(next);
  };

  const onSelect = (key: string, value?: string) => {
    updateParams({ [key]: value, page: undefined });
  };

  const resetAll = () => setParams(new URLSearchParams());

  const activeChips = [
    genre && { key: "genre", label: genre },
    region && { key: "region", label: region },
    year && { key: "year", label: year },
  ].filter(Boolean) as { key: string; label: string }[];

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "电影分类" }]} />

        <div className="mt-5 md:mt-6 mb-8 md:mb-10">
          <span className="inline-flex items-center gap-1.5 text-xs font-label tracking-[0.2em] text-primary-600 uppercase mb-2">
            <span className="w-5 h-px bg-primary-400"></span>
            Category
          </span>
          <h1 className="font-heading font-black text-3xl md:text-4xl text-foreground-950">电影分类</h1>
          <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-2xl">
            按题材、地区、年份自由组合筛选，快速定位你想看的电影资源与剧情介绍。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <button
              type="button"
              onClick={() => setMobileFilter((v) => !v)}
              className="lg:hidden w-full h-11 mb-4 flex items-center justify-between px-4 rounded-md bg-background-50 border border-background-200 text-sm font-label text-foreground-800 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line text-primary-500"></i>
                筛选条件
                {activeChips.length > 0 && (
                  <span className="px-1.5 h-5 rounded-full bg-primary-500 text-background-50 text-[10px] flex items-center">
                    {activeChips.length}
                  </span>
                )}
              </span>
              <i className={`ri-arrow-down-s-line text-lg transition-transform ${mobileFilter ? "rotate-180" : ""}`}></i>
            </button>

            <div className={`${mobileFilter ? "block" : "hidden"} lg:block lg:sticky lg:top-24`}>
              <FilterPanel selected={{ genre, region, year }} onSelect={onSelect} onReset={resetAll} />
            </div>
          </aside>

          {/* Results */}
          <section className="lg:col-span-3">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-semibold text-primary-600">{result.length}</span> 部影片
              </p>
              <div className="flex items-center gap-1 bg-background-100 border border-background-200 rounded-md p-1">
                {[
                  { v: "latest", label: "最新上映", icon: "ri-calendar-line" },
                  { v: "popular", label: "最热门", icon: "ri-fire-line" },
                ].map((s) => (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => updateParams({ sort: s.v, page: undefined })}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
                      sort === s.v ? "bg-background-50 text-primary-700 font-medium" : "text-foreground-500"
                    }`}
                  >
                    <i className={s.icon}></i>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {activeChips.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-5">
                {activeChips.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => onSelect(c.key, undefined)}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label hover:bg-primary-200 transition-colors cursor-pointer"
                  >
                    {c.label}
                    <i className="ri-close-line"></i>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={resetAll}
                  className="text-xs text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  清除全部
                </button>
              </div>
            )}

            {pageItems.length === 0 ? (
              <EmptyState
                icon="ri-movie-2-line"
                title="暂无符合条件的电影"
                desc="换个筛选条件试试，或重置后浏览全部影片。"
                action={
                  <button
                    type="button"
                    onClick={resetAll}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line"></i>
                    重置筛选
                  </button>
                }
              />
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-5">
                  {pageItems.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
                <Pagination
                  page={current}
                  totalPages={totalPages}
                  onChange={(p) => {
                    updateParams({ page: String(p) });
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              </>
            )}
          </section>
        </div>
      </div>
    </SiteLayout>
  );
}